import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getApiV1ExamExamidLangsQueryKey,
  getApiV1ExamLangsIdQueryKey,
  usePutApiV1ExamLangsId,
} from '@/shared/api/generated';

/**
 * Hook para editar um idioma de prova via PUT /api/v1/exam/langs/{id}.
 *
 * Invalida a query de idiomas da prova após sucesso.
 *
 * @param examId - ID da prova à qual o idioma pertence
 * @returns Handler de submit e estado de loading
 */
export function useEditExamLang(examId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examManagement');

  const mutation = usePutApiV1ExamLangsId({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamExamidLangsQueryKey(examId),
        });
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamLangsIdQueryKey(variables.id),
        });
      },
      onError: () => alert({ alertType: 'warning', title: t('toast.error') }),
    },
  });

  const editLang = (
    id: number,
    data: { cultureInfo: string; instruction?: string | null; active: boolean },
    callbacks?: { onSuccess?: () => void },
  ) => {
    mutation.mutate(
      { id, data },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return { editLang, isLoading: mutation.isPending } as const;
}
